import {
  SaveFullTestStepRequest,
  StepCategory,
  StepRole,
  TestStep,
  TestStepRunStatus,
} from '../../../models/test-case.model';

/**
 * Rohwerte des Schritt-Formulars. Das Backend kennt am Schritt nur die drei
 * Text-Slots `action`, `expectedResult` und `notes` (plus `title`). Je nach
 * Rolle (PRECONDITION | STEP | POSTCONDITION) tragen die UI-Felder andere
 * Beschriftungen, werden aber rollenunabhängig in dieselben drei Slots gemappt
 * (siehe `slotsFromForm` / `formFromStep`). Die Rolle entscheidet nur über das
 * Layout, nicht über zusätzliche Persistenz-Felder.
 */
export interface TestStepFormRaw {
  title: string;
  stepRole: StepRole;
  category: StepCategory | '';
  status: TestStepRunStatus;
  /** Rollen-Hilfsfeld → action (Vorbedingungs-/Schritt-/Nachbedingungs-Beschreibung bzw. Ablauf). */
  action: string;
  /** Rollen-Hilfsfeld → expectedResult (erwarteter Zustand/Ausgang/Ergebnis), pflicht je Rolle. */
  expectedResult: string;
  /** Rollen-Hilfsfeld → notes (benötigte Test-Daten bzw. Eingabedaten). */
  notes: string;
  /** Classic-TCDL-Skript (NEX-1022); mehrzeilig, leere Zeile = manueller Schritt. */
  tcdl: string;
  /** Verantwortlicher (Aktor-Id); leer = nicht zugewiesen. */
  assigneeUserId: string;
  /** Frei vergebbare Tags (NEX-459). */
  tags: string[];
  /** Story-Jira-Key → storyJiraKey; leer = löschen. */
  storyTicket: string;
  /** Bug-Jira-Key → bugJiraKey; leer = löschen. */
  bugTicket: string;
}

function nullable(value: string): string | null {
  return value.trim() || null;
}

function category(value: StepCategory | ''): StepCategory | null {
  return value || null;
}

/**
 * Buffered-Save-Mapper (NEX-600/NEX-612): erzeugt den einzigen POST/PUT-Body
 * für den Bibliotheks-Schritt. Full-Replace-Semantik — leerer Assignee ⇒
 * {@code null} entfernt den Verantwortlichen; leere Story/Bug-Keys werden als
 * leere Strings gesendet (BE-Vertrag: leerer String = löschen).
 */
export function toSaveFullTestStepRequest(raw: TestStepFormRaw): SaveFullTestStepRequest {
  return {
    title: raw.title.trim(),
    action: nullable(raw.action),
    expectedResult: nullable(raw.expectedResult),
    notes: nullable(raw.notes),
    tcdl: raw.tcdl.trim() ? raw.tcdl : '',
    status: raw.status,
    stepRole: raw.stepRole,
    category: category(raw.category),
    assigneeUserId: nullable(raw.assigneeUserId),
    tags: raw.tags,
    storyJiraKey: raw.storyTicket.trim() || '',
    bugJiraKey: raw.bugTicket.trim() || '',
  };
}

/**
 * Beim Laden im Edit-Modus die drei Text-Slots eines Schritts in die
 * rollen-spezifischen Formularfelder zurück übersetzen. Da das Mapping je Rolle
 * 1:1 auf action/expectedResult/notes geht, ist die Rückrichtung rollenunabhängig.
 */
export function patchFromStep(
  step: TestStep,
): Pick<
  TestStepFormRaw,
  | 'title'
  | 'stepRole'
  | 'category'
  | 'status'
  | 'action'
  | 'expectedResult'
  | 'notes'
  | 'tcdl'
  | 'assigneeUserId'
  | 'tags'
  | 'storyTicket'
  | 'bugTicket'
> {
  return {
    title: step.title ?? '',
    stepRole: step.stepRole ?? 'STEP',
    category: step.category ?? '',
    status: step.status ?? 'DRAFT',
    action: step.action ?? '',
    expectedResult: step.expectedResult ?? '',
    notes: step.notes ?? '',
    tcdl: step.tcdl ?? '',
    assigneeUserId: step.assigneeUserId ?? '',
    tags: step.tags ?? [],
    storyTicket: step.storyJiraKey ?? '',
    bugTicket: step.bugJiraKey ?? '',
  };
}
